import { createContext, useContext, useMemo, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { steps } from '../onboarding/config';
import type { context, Step, StepSlug } from '../onboarding/types/types';

// The shape of our state is the shape of the onboarding context
type State = context;
// The actions we can take in the onboarding state machine
type Action =
  | { type: 'SET_INDEX'; index: number }
  | { type: 'SAVE_STEP'; slug: StepSlug; data: unknown }
  | { type: 'CLEAR_STEP'; slug: StepSlug }
  | { type: 'RESTART' };

// Define the initial state
const initial: State = { index: 0, answers: {} };

// Define the reducer which performs an action given the current state
// and the desired action in the state machine. reducer(current_state, action) => new_state
function reducer(currentState: State, action: Action): State {
  switch (action.type) {
    case 'SET_INDEX': // Update the index with the new index in the onboarding flow
      return { ...currentState, index: action.index };
    case 'SAVE_STEP': // Update the answers with the current steps's answers
      return { ...currentState, answers: { ...currentState.answers, [action.slug]: action.data } };
    case 'CLEAR_STEP': {
      const { [action.slug]: _, ...rest } = currentState.answers; // Extract all steps answers than the current
      return { ...currentState, answers: rest }; // Update answers wiht the only the rest of the steps (not including the current)
    }
    case 'RESTART':
      return { index: 0, answers: {} };
    default:
      return currentState;
  }
}

type OnboardingContextType = {
  state: State; // The state we are in
  visible: Step[]; // All the visible steps
  current: Step; // The current step
  next(slug: StepSlug): void; // Go to the next step specified by the slug
  saveStep(slug: StepSlug, data: unknown): void; // Save the data for the current step
  clearStep(slug: StepSlug): void; // Clear the data for the current step
  restart(): void; // Restart the onboarding
};

const OnboardingContext = createContext<OnboardingContextType | null>(null);

export function OnboardingProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initial);
  const navigate = useNavigate();
  const visible = useMemo(
    () => steps.filter((step) => (step.shouldShow ? step.shouldShow(state) : true)), // if function exists, then evaluate if it should be shown
    [state]
  ); // only recompute visible when the state changes

  const current = visible[state.index];

  const onboardingRuntime: OnboardingContextType = {
    state,
    visible,
    current,
    next: (slug: StepSlug) => {
      dispatch({ type: 'SET_INDEX', index: state.index + 1 });
      navigate(`/onboarding?slug=${slug}`);
    },
    saveStep: (slug, data) => dispatch({ type: 'SAVE_STEP', slug, data }),
    clearStep: (slug) => dispatch({ type: 'CLEAR_STEP', slug }),
    restart: () => dispatch({ type: 'RESTART' })
  };

  return (
    <OnboardingContext.Provider value={onboardingRuntime}>{children}</OnboardingContext.Provider>
  );
}

// Custom hook to use the onboarding context
export function useOnboardingContext(): OnboardingContextType {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboardingContext must be used inside an OnboardingProvider');
  }
  return context;
}
